import { useEffect, useMemo, useState } from 'react';
import { X, Check, Loader2 } from 'lucide-react';
import CategoryCombobox from './CategoryCombobox.jsx';
import TransactionRow from './TransactionRow.jsx';
import ForecastBreakdownRow from './forecasts/ForecastBreakdownRow.jsx';
import { useTransactions, usePatchTransactionCategory } from '../hooks/useTransactions.js';
import { useCashFlowBreakdown } from '../hooks/useCashFlowBreakdown.js';
import { useCategories } from '../hooks/useCategories.js';
import { formatCurrency } from '../lib/format.js';

/**
 * Récap 3 colonnes HT / TVA / TTC affiché en haut du drill-down.
 * Visible uniquement quand au moins un des montants HT/TVA est non nul.
 *
 * Props directes (réutilisable pour la colonne Réel ou la colonne Prévi) :
 *   htCents   number | null
 *   vatCents  number | null  — signé (négatif pour les charges)
 *   ttcCents  number | null
 */
function VatSummaryKpis({ htCents = null, vatCents = null, ttcCents = null }) {
  // N'afficher que si les données HT/TVA sont présentes et non nulles
  if (htCents === null && vatCents === null) return null;

  return (
    <div className="flex flex-col sm:flex-row gap-2 px-4 py-3 bg-slate-50 border-b border-slate-100">
      <div className="flex-1 text-center">
        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">Total HT</p>
        <p className="text-sm font-semibold tabular-nums text-slate-800">
          {htCents != null ? formatCurrency(htCents) : '—'}
        </p>
      </div>
      <div className="flex-1 text-center border-t sm:border-t-0 sm:border-l border-slate-200 pt-2 sm:pt-0">
        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">Total TVA</p>
        <p className="text-sm font-semibold tabular-nums text-slate-600">
          {vatCents != null ? formatCurrency(vatCents) : '—'}
        </p>
      </div>
      <div className="flex-1 text-center border-t sm:border-t-0 sm:border-l border-slate-200 pt-2 sm:pt-0">
        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">Total TTC</p>
        <p className="text-sm font-semibold tabular-nums text-slate-800">
          {ttcCents != null ? formatCurrency(ttcCents) : '—'}
        </p>
      </div>
    </div>
  );
}

/**
 * Slide-over qui affiche les transactions et/ou prévisionnels d'une cellule
 * du tableau cash-flow (catégorie + période).
 *
 * Modes :
 *   'actuals'   — uniquement les transactions passées (comportement historique)
 *   'forecasts' — uniquement les items prévisionnels futurs
 *   'both'      — les deux sections (mois courant)
 *
 * Props :
 *   open           : boolean
 *   onClose        : () => void
 *   title          : string (ex : "Marketing / Communication")
 *   subtitle       : string (ex : "avril 2026")
 *   categoryIds    : string[] UUIDs — self + descendants
 *   from, to       : ISO dates (période bucket)
 *   mode           : 'actuals' | 'forecasts' | 'both' (défaut 'actuals')
 *   flowType       : 'income' | 'expense' | 'neutral' (non utilisé pour la couleur,
 *                    TransactionRow gère la couleur via le signe)
 *   scenario       : 'median' | 'pessimiste' | 'optimiste' (défaut 'median') — transmis
 *                    au hook breakdown pour les projections scénarisées
 */
export default function PeriodCategoryPanel({
  open,
  onClose,
  title,
  subtitle,
  categoryIds = [],
  from,
  to,
  mode = 'actuals',
  scenario = 'median',
  unclassified = false,
  vatFilter = null,
}) {
  const enabled = open && (unclassified || categoryIds.length > 0 || !!vatFilter) && !!from && !!to;

  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === 'Escape') onClose?.(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      <div
        className={`
          fixed inset-0 z-40 transition-opacity duration-200
          ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
          bg-black/20 lg:bg-black/0
          lg:right-[560px] lg:left-0 xl:right-[640px]
        `}
        aria-hidden
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'actuals' ? 'Transactions de la période' : 'Détail de la période'}
        className={`
          fixed z-50 bg-white shadow-2xl
          transition-transform duration-200 ease-out

          inset-x-0 bottom-0 rounded-t-2xl max-h-[92dvh] overflow-y-auto
          ${open ? 'translate-y-0' : 'translate-y-full'}

          lg:inset-x-auto lg:right-0 lg:top-0 lg:bottom-0 lg:h-dvh lg:w-[720px] xl:w-[900px] lg:rounded-none lg:max-h-none lg:overflow-y-auto
          ${open ? 'lg:translate-x-0' : 'lg:translate-x-full'}
        `}
      >
        <div className="flex items-start justify-between border-b border-slate-100 px-4 py-3 sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900 truncate">{title}</h2>
            <p className="text-xs text-slate-500">{subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <div>
          {enabled ? (
            <PanelBody
              categoryIds={categoryIds}
              from={from}
              to={to}
              mode={unclassified ? 'actuals' : mode}
              scenario={scenario}
              unclassified={unclassified}
              vatFilter={vatFilter}
            />
          ) : (
            <p className="text-sm text-slate-400 py-8 text-center">Aucune sélection</p>
          )}
        </div>
      </div>
    </>
  );
}

// Construit une map id → { name, parent_id, path: "Parent / Enfant" }
function buildCategoryPathMap(categories) {
  const byId = new Map(categories.map((c) => [c.id, c]));
  function path(id) {
    const segments = [];
    let cur = byId.get(id);
    while (cur) {
      segments.unshift(cur.name);
      cur = cur.parent_id ? byId.get(cur.parent_id) : null;
    }
    return segments;
  }
  const out = new Map();
  for (const c of categories) {
    out.set(c.id, { name: c.name, parent_id: c.parent_id, path: path(c.id) });
  }
  return out;
}

function PanelBody({ categoryIds, from, to, mode, scenario = 'median', unclassified = false, vatFilter = null }) {
  const showActuals   = mode === 'actuals'   || mode === 'both';
  const showForecasts = mode === 'forecasts' || mode === 'both';
  // En mode vatFilter, les actuals viennent du breakdown (filtré par TVA),
  // pas de useTransactions (qui filtrerait par catégorie).
  const actualsViaBreakdown = !!vatFilter;

  // Actuals — hook désactivé en mode vatFilter ou forecasts pur
  const txQuery = useTransactions(
    actualsViaBreakdown
      ? { /* disabled */ }
      : unclassified
        ? { unclassified: true, from, to, per_page: 200 }
        : {
            category_ids: showActuals ? categoryIds : [],
            from:         showActuals ? from : undefined,
            to:           showActuals ? to   : undefined,
            per_page: 200,
          }
  );

  // Breakdown — actif si forecasts demandés OU vatFilter (qui charge actuals + forecasts)
  const breakdownQuery = useCashFlowBreakdown({
    from:        (showForecasts || actualsViaBreakdown) ? from : undefined,
    to:          (showForecasts || actualsViaBreakdown) ? to   : undefined,
    categoryIds: showForecasts ? categoryIds : [],
    scenario,
    vatFilter,
  });

  const { data: catsRaw } = useCategories();
  const categories = Array.isArray(catsRaw) ? catsRaw : catsRaw?.categories ?? catsRaw?.data ?? [];
  const catPathMap = useMemo(() => buildCategoryPathMap(categories), [categories]);

  const txItems        = actualsViaBreakdown
    ? (showActuals ? (breakdownQuery.data?.actuals ?? []) : [])
    : (txQuery.data?.items ?? []);
  const forecastItems  = showForecasts ? (breakdownQuery.data?.forecasts ?? []) : [];
  const breakdownTotals = breakdownQuery.data?.totals ?? null;
  const combinedCents  = breakdownTotals?.combined_cents
    ?? (txItems.reduce((s, t) => s + (t.amount_cents ?? 0), 0)
        + forecastItems.reduce((s, f) => s + (f.amount_cents ?? 0), 0));

  const isLoading = (showActuals && txQuery.isLoading) || (showForecasts && breakdownQuery.isLoading);
  const hasError  = (showActuals && txQuery.error)     || (showForecasts && breakdownQuery.error);

  // Grouper les transactions par leaf category
  const txGroups = useMemo(() => {
    const byCat = new Map();
    for (const tx of txItems) {
      const key = tx.category_id ?? '__none__';
      if (!byCat.has(key)) {
        const meta = tx.category_id ? catPathMap.get(tx.category_id) : null;
        byCat.set(key, {
          id:    tx.category_id,
          name:  meta?.name ?? tx.category_name ?? 'Non classifiée',
          path:  meta?.path.slice(0, -1) ?? [],
          items: [],
          total: 0,
        });
      }
      const g = byCat.get(key);
      g.items.push(tx);
      g.total += tx.amount_cents ?? 0;
    }
    for (const g of byCat.values()) {
      g.items.sort((a, b) => {
        const da = a.settled_at ?? a.operation_date ?? '';
        const db = b.settled_at ?? b.operation_date ?? '';
        return db.localeCompare(da);
      });
    }
    return [...byCat.values()].sort(
      (a, b) => Math.abs(b.total) - Math.abs(a.total)
    );
  }, [txItems, catPathMap]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="animate-spin text-slate-400" size={20} />
      </div>
    );
  }
  if (hasError) {
    const msg = txQuery.error?.message ?? breakdownQuery.error?.message ?? 'Erreur inconnue';
    return <p className="text-sm text-red-600 py-4 px-4">Erreur : {msg}</p>;
  }
  if (!txItems.length && !forecastItems.length) {
    return (
      <p className="text-sm text-slate-400 py-8 text-center">
        {mode === 'actuals'
          ? 'Aucune transaction sur cette période.'
          : mode === 'forecasts'
          ? 'Aucun prévisionnel sur cette période.'
          : 'Aucune donnée sur cette période.'}
      </p>
    );
  }

  // Barre de totaux combinés (affichée seulement si du contenu existe)
  const totalTxCents = txItems.reduce((acc, t) => acc + (t.amount_cents ?? 0), 0);
  const totalFcCents = forecastItems.reduce((acc, f) => acc + (f.amount_cents ?? 0), 0);

  // Mode "both" avec les 2 sections non vides → 2 récaps distincts par colonne.
  // Sinon → 1 récap global (comportement historique).
  const isBothFull = mode === 'both' && txItems.length > 0 && forecastItems.length > 0;

  // Totaux spécifiques par colonne (nouveau contrat API).
  // Fallback gracieux : si les champs *_ht_cents/*_vat_cents ne sont pas encore
  // présents (backend pas encore déployé), on passe null → VatSummaryKpis masque le bloc.
  const actualsVatKpis = {
    htCents:  breakdownTotals?.actuals_ht_cents   ?? null,
    vatCents: breakdownTotals?.actuals_vat_cents  ?? null,
    ttcCents: breakdownTotals?.actuals_cents      ?? null,
  };
  const forecastsVatKpis = {
    htCents:  breakdownTotals?.forecasts_ht_cents  ?? null,
    vatCents: breakdownTotals?.forecasts_vat_cents ?? null,
    ttcCents: breakdownTotals?.forecasts_cents     ?? null,
  };

  return (
    <div>
      {/* Barre de résumé */}
      <div className="flex items-center justify-between text-xs text-slate-500 px-4 py-2 border-b border-slate-100">
        <span>
          {txItems.length > 0 && (
            <>{txItems.length} transaction{txItems.length > 1 ? 's' : ''}</>
          )}
          {txItems.length > 0 && forecastItems.length > 0 && <span className="mx-1">·</span>}
          {forecastItems.length > 0 && (
            <>{forecastItems.length} prévisionnel{forecastItems.length > 1 ? 's' : ''}</>
          )}
        </span>
        <span className="font-semibold tabular-nums text-slate-800">
          Total : {formatCurrency(mode === 'both' ? combinedCents : (mode === 'actuals' ? totalTxCents : totalFcCents))}
        </span>
      </div>

      {/* Récap HT / TVA / TTC en mode single — affiche uniquement les totaux du côté courant */}
      {!isBothFull && mode === 'actuals'   && <VatSummaryKpis {...actualsVatKpis} />}
      {!isBothFull && mode === 'forecasts' && <VatSummaryKpis {...forecastsVatKpis} />}

      {/* Vue côte-à-côte sur desktop (lg+), stacked sur mobile.
          La grille se déclenche dès qu'on est en mode='both' avec les 2 sections présentes. */}
      <div
        className={
          isBothFull
            ? 'lg:grid lg:grid-cols-2 lg:divide-x lg:divide-slate-200'
            : ''
        }
      >
        {/* Section transactions passées */}
        {showActuals && txItems.length > 0 && (
          <div>
            {mode === 'both' && (
              <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 px-4 pt-3 pb-1.5 bg-slate-50/60 sticky top-0 lg:top-[57px] z-[1]">
                Réel · {txItems.length} tx · {formatCurrency(totalTxCents)}
              </p>
            )}
            {/* Récap HT/TVA/TTC spécifique à la colonne Réel — uniquement en mode both plein */}
            {isBothFull && (
              <VatSummaryKpis {...actualsVatKpis} />
            )}
            {txGroups.length > 1 ? (
              <div className="divide-y divide-slate-200">
                {txGroups.map((g) => (
                  <div key={g.id ?? '__none__'}>
                    <div className="flex items-baseline justify-between px-4 py-2 bg-slate-50">
                      <div className="min-w-0">
                        {g.path.length > 0 && (
                          <p className="text-[10px] uppercase tracking-wide text-slate-400 truncate">
                            {g.path.join(' / ')}
                          </p>
                        )}
                        <p className="text-sm font-semibold text-slate-800 truncate">{g.name}</p>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <p className="text-[10px] text-slate-500">{g.items.length} tx</p>
                        <p className="text-xs font-semibold tabular-nums text-slate-700">
                          {formatCurrency(g.total)}
                        </p>
                      </div>
                    </div>
                    <ul className="divide-y divide-slate-100">
                      {g.items.map((tx) => (
                        <TxRow key={tx.id} tx={tx} />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {txItems.map((tx) => (
                  <TxRow key={tx.id} tx={tx} />
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Section prévisionnels */}
        {showForecasts && forecastItems.length > 0 && (
          <div>
            {mode === 'both' && (
              <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 px-4 pt-3 pb-1.5 bg-indigo-50/40 sticky top-0 lg:top-[57px] z-[1] border-t border-slate-100 lg:border-t-0">
                Prévisionnel · {forecastItems.length} item{forecastItems.length > 1 ? 's' : ''} · {formatCurrency(totalFcCents)}
              </p>
            )}
            {/* Récap HT/TVA/TTC spécifique à la colonne Prévi — uniquement en mode both plein */}
            {isBothFull && (
              <VatSummaryKpis {...forecastsVatKpis} />
            )}
            <ul className="divide-y divide-slate-100">
              {forecastItems.map((item, idx) => (
                // Inclut date dans la key : les events 'revenue' projetés par
                // l'hypothèse partagent tous le même id (hypothesis.id) — sans
                // date dans la key, React n'en rendait qu'un seul.
                <ForecastBreakdownRow
                  key={`${item.mode}-${item.id ?? 'noid'}-${item.date ?? idx}`}
                  item={item}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function TxRow({ tx }) {
  const [editing, setEditing] = useState(false);
  const [newCat, setNewCat] = useState(tx.category_id ?? '');
  const patch = usePatchTransactionCategory();

  async function submit() {
    if (!newCat || newCat === tx.category_id) {
      setEditing(false);
      return;
    }
    try {
      await patch.mutateAsync({ id: tx.id, category_id: newCat });
      setEditing(false);
    } catch {
      // silent
    }
  }

  return (
    <li>
      <TransactionRow transaction={tx} onClick={() => setEditing((e) => !e)} compact showHtFormat />
      {editing && (
        <div className="bg-slate-50/60 border-t border-slate-100 px-4 py-3 flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <CategoryCombobox
              value={newCat}
              onChange={setNewCat}
              placeholder="Choisir une catégorie"
            />
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={patch.isPending || !newCat || newCat === tx.category_id}
            className="rounded-md px-2.5 py-1.5 text-xs font-medium bg-slate-800 text-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1 shrink-0"
          >
            {patch.isPending ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Check size={12} />
            )}
            Enregistrer
          </button>
          <button
            type="button"
            onClick={() => { setEditing(false); setNewCat(tx.category_id ?? ''); }}
            className="text-xs text-slate-500 hover:text-slate-700 shrink-0"
          >
            Annuler
          </button>
        </div>
      )}
    </li>
  );
}
